import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import {
  TABLE_OPTIONS_QUERY_PARAMETER,
  type TableQueryOptions,
  tableQueryOptionsSchema,
} from '@ipfabric/utils';
import { useLocale } from 'containers/App/LocaleContext';
import { useLogError } from 'containers/ErrorBoundary/useLogError';
import { either } from 'fp-ts';
import type { Either } from 'fp-ts/lib/Either';
import { identity, pipe } from 'fp-ts/lib/function';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNotifications } from 'utils/hooks/useNotifications';

export type TableQueryOptionsHook = TableQueryOptions | null;

const validateOptions = (
  obj: TableQueryOptions,
): Either<Error | null, TableQueryOptions> =>
  pipe(tableQueryOptionsSchema.validate(obj), ({ error }) =>
    error ? either.left(error) : either.right(obj),
  );

const parseOptions = either.tryCatchK<
  [string],
  TableQueryOptions,
  Error | null
>(JSON.parse, either.toError);

export const useTableQueryOptions = (): TableQueryOptionsHook => {
  const [searchParams] = useSearchParams();
  const optionsParam = searchParams.get(TABLE_OPTIONS_QUERY_PARAMETER);
  const notifications = useNotifications();
  const logError = useLogError();
  const urlOptionsMsg = useLocale().apiTable.urlOptions;

  const processOptionsError = useStableCallback((e: Error | null) => {
    if (e !== null) {
      logError(e, 'ApiTable');
      notifications.showNotification({
        type: 'danger',
        message: urlOptionsMsg.parseError,
      });
    }
    return null;
  });

  const options = useMemo(
    () =>
      pipe(
        optionsParam,
        either.fromNullable(null),
        either.chain(parseOptions),
        either.chain(validateOptions),
        either.fold(processOptionsError, identity),
      ),
    [optionsParam],
  );

  return options;
};
